import {
  PaymentMethodFundingSources,
  PaymentMethodFundingSource,
  PayPalFundingSource,
} from '../../../types/PaymentsButtonViewConfig';
import { AMOUNT_OF_TOP_LEVEL_BUTTONS, buttonsViewConfig } from '../buttonsViewConfig';

const prioritizedButtonsViewConfig = Object.entries(buttonsViewConfig)
  .map(([key, value]) => ({
    name: key as PaymentMethodFundingSource,
    ...value,
  }))
  .sort((a, b) => a.priority - b.priority);

type ButtonsViewConfig = typeof prioritizedButtonsViewConfig;
type ButtonsViewConfigItem = ButtonsViewConfig[number];
type ButtonsViewConfigItemList = ButtonsViewConfigItem[];

export interface SeparatedButtonsViewConfig {
  onTopLevel: ButtonsViewConfigItemList;
  insideShowMore?: ButtonsViewConfigItemList;
}

interface AvailablePmsList {
  googlePay: boolean;
  applePay: boolean;
  payPal: PayPalFundingSource[];
}

const getPaymentMethodsName = (availablePmsList: AvailablePmsList): PaymentMethodFundingSource[] => {
  let pmList: PaymentMethodFundingSource[] = [];
  if (availablePmsList.googlePay) {
    pmList.push(PaymentMethodFundingSources.GooglePay);
  }
  if (availablePmsList.applePay) {
    pmList.push(PaymentMethodFundingSources.ApplePay);
  }
  if (availablePmsList.payPal.length) {
    pmList = [...pmList, ...availablePmsList.payPal];
  }
  return pmList;
};

const getPrioritizedPmsConfig = (availablePmsList: AvailablePmsList): ButtonsViewConfigItemList => {
  const paymentMethods = getPaymentMethodsName(availablePmsList);
  return prioritizedButtonsViewConfig.filter((item) => paymentMethods.find((name) => name === item.name));
};

/**
 * Returns buttons config based on 2 groups: button on a Top level
 * and buttons that are inside "More payment options" label
 * https://wix.atlassian.net/browse/PAY-20552
 */
export const getButtonsConfigByGroups = (availablePmsList: AvailablePmsList): SeparatedButtonsViewConfig => {
  const pmsConfig = getPrioritizedPmsConfig(availablePmsList);

  const generalOnTopLevelAndRest = pmsConfig.reduce(
    (acc, value) => {
      if (!value.canBeHidden) {
        acc.onTopLevel.push(value);
      } else {
        acc.rest.push(value);
      }

      return acc;
    },
    {
      onTopLevel: [],
      rest: [],
    },
  );

  const config = generalOnTopLevelAndRest.rest.reduce(
    (acc, value) => {
      if (acc.onTopLevel.length >= AMOUNT_OF_TOP_LEVEL_BUTTONS) {
        acc.insideShowMore.push(value);
      } else {
        acc.onTopLevel.push(value);
      }
      return acc;
    },
    {
      onTopLevel: generalOnTopLevelAndRest.onTopLevel,
      insideShowMore: [],
    },
  );

  return config;
};

export const getButtonsViewConfig = (availablePmsList: AvailablePmsList): ButtonsViewConfigItemList =>
  getPrioritizedPmsConfig(availablePmsList);
